import React, { useContext, useRef, useState } from 'react';
import { CarrierListOutDTO, Pageable, PermissionTypesEnum } from '@api/ringil-api.v2';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import AddNewCarrier from './sidebars/AddNewCarrier';
import { Dialog } from 'primereact/dialog';
import { ApiContext } from '@api/api';
import useNavigation from '@hooks/useNavigation.tsx';
import ButtonLayout from '@components/obsolete/Form/ButtonLayout/ButtonLayout.tsx';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import clsx from 'clsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { useMediaQuery } from 'react-responsive';
import SvgPlus from '@icons/plus.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig,
  FilterType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import { CarrierReferrer } from '@app/pages/customer/carriers/carrier-detail/CarrierDetail.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';

interface CarrierFilter {
  name: string;
}

const AllCarriersList: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { cuListCarriers, cuDeleteCarrier } = useContext(ApiContext);
  const { hasPermission, loggedUser } = useContext(AuthenticatedUserContext);

  const [visibleAddNewCarrier, setVisibleAddNewCarrier] = useState(false);
  const navigation = useNavigation();
  const [deleteCarrierId, setDeleteCarrierId] = useState(undefined);
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  const editEnabled = hasPermission([PermissionTypesEnum.PERMISSION_MY_CARRIERS_RW])
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const loadDataApi = (filter: unknown, pageable: Pageable, onSuccess: (pagedData: PagedData<CarrierListOutDTO>) => void) => {
    const carrierFilter = filter as CarrierFilter;
    cuListCarriers(
      pageable,
      carrierFilter?.name || '',
      data => {
        onSuccess(data);
      },
    );
  };

  const deleteCarrier = () => {
    cuDeleteCarrier(deleteCarrierId, () => {
      tableRef?.current?.reloadData();
      setDeleteCarrierId(undefined);
    });
  };

  const navBodyTemplate = (rowData: CarrierListOutDTO) => {
    return (
      <div>
        {editEnabled && (
          <RingilButton
            mode={'table'}
            icon={<SvgDelete />}
            onClick={() => {
              setDeleteCarrierId(rowData.carrierId);
            }}
          />
        )}
      </div>
    );
  };

  const nameBodyTemplate = (rowData: CarrierListOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <Link
          to={navigation.createNavigationLink(navigation.urlFunctions.createCarrierDetail(rowData.carrierId, CarrierReferrer.CARRIERS))}
        >
            <span
              className={'text-[16px] font-normal leading-normal tracking-[0.24px] text-content-brand font-dm-sans'}
            >
              {rowData.name}
            </span>
        </Link>
      </div>
    );
  };
  const addressBodyTemplate = (rowData: CarrierListOutDTO) => {
    return (
      <div className={'flex flex-col'}>
        <span>{rowData.address.streetNr}</span>
        <span>{rowData.address.city}</span>
        <span>{rowData.address.zipCode}</span>
        <span>{rowData.address.country}</span>
        <COTimezone timezoneId={rowData.address.timezoneId} />
      </div>
    );
  };
  const crnBodyTemplate = (rowData: CarrierListOutDTO) => {
    return (
      <span>{rowData.crn}</span>
    );
  };
  const emailBodyTemplate = (rowData: CarrierListOutDTO) => {
    return (
      <span className={'break-all'}>
        {rowData.mainContact}
      </span>
    );
  };
  const createdBodyTemplate = (rowData: CarrierListOutDTO) => {
    return (
      <span>
        {stringDateTimeWithLocaleFromApiStringInTz(rowData.createdAt, loggedUser.defaultTimezoneId)}
        <COTimezone timezoneId={loggedUser.defaultTimezoneId} />
      </span>
    );
  };

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr(`AllCarriersList.searchName`, `Search name`),
      value: ['name'],
    },
  ];

  const columnsConfig: ColumnConfig<CarrierListOutDTO>[] = [
    {
      id: 'carrierName',
      header: 'AllCarriersList.name',
      headerDefault: 'Name',
      width: '23%',
      sortable: false,
      field: 'name',
      order: 1,
      render: nameBodyTemplate,
    },
    {
      id: 'carrierAddress',
      header: 'AllCarriersList.columnHeaderCarrierAddress',
      headerDefault: 'Address',
      width: '23%',
      sortable: false,
      field: 'address',
      order: 2,
      render: addressBodyTemplate,
    },
    {
      id: 'carrierCrn',
      header: 'AllCarriersList.columnHeaderCarrierCrn',
      headerDefault: 'CRN',
      width: '15%',
      sortable: false,
      field: 'crn',
      order: 3,
      render: crnBodyTemplate,
    },
    {
      id: 'carrierMainContact',
      header: 'AllCarriersList.columnHeaderCarrierMainContact',
      headerDefault: 'Main contact',
      width: '29%',
      sortable: false,
      field: 'mainContact',
      order: 4,
      render: emailBodyTemplate,
    },
    {
      id: 'carrierCreatedAt',
      header: 'AllCarriersList.columnHeaderCarrierCreatedAt',
      headerDefault: 'Created at',
      width: '22%',
      sortable: false,
      field: 'createdAt',
      order: 5,
      render: createdBodyTemplate,
    },
    {
      id: 'carrierAction',
      header: 'AllCarriersList.columnHeaderCarrierAction',
      headerDefault: '',
      width: '8%',
      sortable: false,
      field: 'action',
      order: 6,
      render: navBodyTemplate,
    },
  ];

  return (
    <>
      <div className={clsx('font-[\'DM Sans\']', isMobile && '-mx-4 px-8 pt-8')}>
        <BreadCrumbComponent items={[{ label: tr(`AllCarriersList.titleCarriers`, `All carriers`) }]} />
        <div className="flex items-center mb-6">
          <COHeadline title={tr(`AllCarriersList.titleCarriers`, `All carriers`)} variant={'h1'} />
          {editEnabled && (
            <div className="flex items-center pl-2">
              <RingilButton
                label={tr(`AllCarriersList.newCarriers`, `New carrier`)}
                icon={<SvgPlus />}
                mode={'link'}
                onClick={() => setVisibleAddNewCarrier(true)}
              />
            </div>
          )}
        </div>
      </div>

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig as ColumnConfig<unknown>[]}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'my_carriers_list'}
      />
      {/*
        // @ts-ignore*/}
      <Dialog
        header={tr(`AllCarriersList.deleteThisCarriers`, `Smazat dopravce`)}
        visible={deleteCarrierId}
        className="modal-sm"
        onHide={() => setDeleteCarrierId(undefined)}
      >
        <div>
          <p
            className="p-mt-0">{tr(`AllCarriersList.byDeleting`, `Smazáním dopravce budou smazány i navazující informace.`)}</p>
        </div>

        <ButtonLayout wrapper="WITH_PADDING_TOP_4">
          <Button label={tr(`AllCarriersList.delete`, `Smazat`)} className="p-mr-2 p-button-danger"
                  onClick={deleteCarrier} />
          <Button label={tr(`AllCarriersList.cancel`, `Zrušit`)} className="p-button-text"
                  onClick={() => setDeleteCarrierId(undefined)} />
        </ButtonLayout>
      </Dialog>
      <AddNewCarrier
        visible={visibleAddNewCarrier}
        onHide={() => setVisibleAddNewCarrier(false)}
        onComplete={() => {
          setVisibleAddNewCarrier(false);
          tableRef?.current?.reloadData();
        }}
      />
    </>
  );
};

export default AllCarriersList;
